import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Stack,
  Switch,
  Text,
  Title,
} from '@mantine/core';
import {
  IconBell,
  IconDeviceDesktop,
  IconDownload,
  IconInfoCircle,
  IconTrash,
} from '@tabler/icons-react';
import {
  PUSH_GROUPS,
  PushGroup,
  usePushSettings,
  useRemoveDevice,
  useSendTestPush,
  useSetPushGroups,
  useSubscribeDevice,
} from '../../hooks/usePushApi';
import { PushState, pushState, readPushEnv } from '../../utils/pushSupport';
import { canInstall, promptInstall } from '../../utils/installPrompt';
import { useCompanyModules, type CompanyModule } from '../../hooks/useCompanyModules';

/** Разделы компании группы уведомлений (хватит любого): «Эссе» напоминают и об отчётах. */
const GROUP_MODULES: Record<PushGroup, CompanyModule[]> = {
  rework: ['essays', 'quarterly'],
  competencies: ['competencyMap'],
  performanceReview: ['performanceReview'],
  essays: ['essays', 'quarterly'],
  scoring: ['quarterly'],
  goals: ['goals'],
};

/** Раздел «Уведомления»: это устройство, мои устройства, группы, установка приложения. */
export const NotificationsSection: React.FC = () => {
  const { t } = useTranslation();
  const { data } = usePushSettings();
  const subscribe = useSubscribeDevice();
  const remove = useRemoveDevice();
  const setGroups = useSetPushGroups();
  const test = useSendTestPush();
  const [state, setState] = useState<PushState | null>(null);
  const [currentEndpoint, setCurrentEndpoint] = useState<string | null>(null);
  const [isInstallable, setIsInstallable] = useState(canInstall());
  // Уведомления неподключённых компании разделов не приходят — и переключателей нет.
  const { hasModule } = useCompanyModules();

  useEffect(() => {
    if (!data) return;
    void readPushEnv(data.devices.map((d) => d.endpoint)).then((env) => {
      setState(pushState(env));
      setCurrentEndpoint(env.browserEndpoint);
    });
  }, [data]);

  // Пуши не настроены на сервере — раздела нет, чтобы не обещать то, что не работает.
  if (!data?.isConfigured || !state) return null;

  const toggleGroup = (group: PushGroup, isOn: boolean) =>
    setGroups.mutate(isOn ? [...data.groups, group] : data.groups.filter((g) => g !== group));
  const thisDevice = data.devices.find((d) => d.endpoint === currentEndpoint);

  return (
    <Card shadow="sm" padding="xl" radius="md" withBorder>
      <Stack gap="lg">
        <div>
          <Title order={3} mb="xs">
            {t('push.title')}
          </Title>
          <Text size="sm" c="dimmed">
            {t('push.description')}
          </Text>
        </div>

        {state === 'unsupported' && (
          <Alert variant="light" color="gray" icon={<IconInfoCircle size={16} />}>
            {t('push.unsupported')}
          </Alert>
        )}
        {state === 'ios-not-installed' && (
          <Alert variant="light" color="blue" icon={<IconInfoCircle size={16} />}>
            {t('push.iosInstall')}
          </Alert>
        )}
        {state === 'denied' && (
          <Alert variant="light" color="orange" icon={<IconInfoCircle size={16} />}>
            {t('push.denied')}
          </Alert>
        )}
        {state === 'not-subscribed' && data.publicKey && (
          <Group>
            <Button
              leftSection={<IconBell size={16} />}
              loading={subscribe.isPending}
              onClick={() => subscribe.mutate(data.publicKey!)}
            >
              {t('push.enable')}
            </Button>
          </Group>
        )}
        {state === 'subscribed' && (
          <Group>
            <Badge color="teal" variant="light">
              {t('push.enabled')}
            </Badge>
            <Button variant="light" loading={test.isPending} onClick={() => test.mutate()}>
              {t('push.test')}
            </Button>
            {thisDevice && (
              <Button variant="subtle" color="red" onClick={() => remove.mutate(thisDevice)}>
                {t('push.disableHere')}
              </Button>
            )}
          </Group>
        )}

        {isInstallable && (
          <Group>
            <Button
              variant="default"
              leftSection={<IconDownload size={16} />}
              onClick={() => void promptInstall().then(() => setIsInstallable(canInstall()))}
            >
              {t('push.install')}
            </Button>
          </Group>
        )}

        {state !== 'unsupported' && (
          <>
            <Stack gap="xs">
              <Text fw={600}>{t('push.devices')}</Text>
              {data.devices.length === 0 && (
                <Text size="sm" c="dimmed">
                  {t('push.noDevices')}
                </Text>
              )}
              {data.devices.map((d) => (
                <Group key={d.id} justify="space-between" wrap="nowrap">
                  <Group gap="sm" wrap="nowrap">
                    <IconDeviceDesktop size={18} />
                    <div>
                      <Group gap={6}>
                        <Text size="sm" fw={500}>
                          {d.deviceLabel || t('push.unknownDevice')}
                        </Text>
                        {d.endpoint === currentEndpoint && (
                          <Badge size="xs" variant="light">
                            {t('push.thisDevice')}
                          </Badge>
                        )}
                      </Group>
                      <Text size="xs" c="dimmed">
                        {t('push.addedAt', { date: dayjs(d.createdAt).format('D MMM YYYY') })}
                        {d.lastSentAt &&
                          ` · ${t('push.lastSent', { date: dayjs(d.lastSentAt).format('D MMM, HH:mm') })}`}
                      </Text>
                    </div>
                  </Group>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    aria-label={t('push.remove')}
                    onClick={() => remove.mutate(d)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>

            <Stack gap="sm">
              <Text fw={600}>{t('push.groupsTitle')}</Text>
              {PUSH_GROUPS.filter((group) => GROUP_MODULES[group].some(hasModule)).map((group) => (
                <Switch
                  key={group}
                  color="teal"
                  label={t(`push.groups.${group}`)}
                  description={t(`push.groups.${group}Desc`)}
                  checked={data.groups.includes(group)}
                  onChange={(e) => toggleGroup(group, e.currentTarget.checked)}
                />
              ))}
            </Stack>
          </>
        )}
      </Stack>
    </Card>
  );
};
